import _                                               from 'underscore';
import React, { useEffect, useRef, useState }          from 'react';
import { Checkbox, Form, Header, Icon, List, Segment } from 'semantic-ui-react';
import { Formik, FormikProps }                         from 'formik';
import * as Yup                                        from 'yup';
import { useQuery }                                    from '@apollo/react-hooks';

import { runIfFunction }                                                                                                                                                                                                   from '@src/util';
import { Align, CorvalActivity, CorvalContextOwner, CorvalGanttDependency, CorvalStaticProjectActivity, CorvalStaticProjectSubObjective, CorvalSubObjective, CorvalUIColor, FlexAlign, FlexJustify, Position, Size, Uuid } from '@src/types';
import { queriesHaveData, queriesLoading }                                                                                                                                                                                 from '@src/store/_apolloUtil';
import { QueryDialogProps, QueryOpDialog }                                                                                                                                                                                 from '../../../components/from-query/QueryOpDialog';
import { BasicDialogContainer }                                                                                                                                                                                            from '../../../components/modals/BasicDialogContainer';
import { CollapsibleContentContainer, Flexbox, FlexItem, StyledContainer }                                                                                                                                                 from '../../../containers';
import { LoadingIndicator }                                                                                                                                                                                                from '../../../components/util/loading/LoadingIndicator';
import { ObjectiveSelectionLayout }                                                                                                                                                                                        from "@src/views/pages/projects/objectives/selection/ObjectiveSelectionLayout";
import { FinePrintMessage }                                                                                                                                                                                                from "@src/views/components/util/messaging/FinePrintMessage";
import classNames                                                                                                                                                                                                          from "classnames";
import { ItemWithIcon }                                                                                                                                                                                                    from "@src/views/components/display/ItemWithIcon";
import { Cell, Grid }                                                                                                                                                                                                      from "styled-css-grid";
import { ENTITY_ICONS }                                                                                                                                                                                                    from "@src/constants";
import { ObjectiveQueries }                                                                                                                                                                                                from "@src/queries/ObjectiveQueries";
import { UseIf }                                                                                                                                                                                                           from "@src/views/components/display/EqualValues";
import { BigFriendlyMessage }                                                                                                                                                                                              from "@src/views/components/util/messaging/BigFriendlyMessage";
import { DecisionEngineQueries }                                                                                                                                                                                           from "@src/queries";
import { FieldWrapper }                                                                                                                                                                                                    from '@src/views/components/forms';
import { FieldLabel }                                                                                                                                                                                                      from '@src/views/components/forms/formik/FieldLabel';


export interface AddDeclinedProjectSubObjectivesDialogOnCompleteReturnData {
    emptySubObjectives: CorvalStaticProjectSubObjective[],
    addedActivities: CorvalStaticProjectActivity[],
    addedDependencies: CorvalGanttDependency[],
    activeDependenciesOnImport: boolean,
}

interface AddDeclinedProjectSubObjectivesDialogProps extends QueryDialogProps {
    projectId: Uuid,
    projectVersionId: string,
    categoryId?: Uuid,
    subCategoryId?: Uuid,
    excludedTaxonomyIds: Uuid[],
    mutationResultDataFields?: { activities: string, dependencies: string },
    showImportOptions?: boolean,

    onComplete?( data: AddDeclinedProjectSubObjectivesDialogOnCompleteReturnData ): void,
}

interface FormValues {
    [ SUB_OBJECTIVE_ID: string ]: boolean,
}

type DeclinedStaticProjectSubObjectiveDataItem = Pick<CorvalSubObjective, 'id' | 'displayName' | 'description' | 'category' | 'subCategory'> & { activities: CorvalActivity[] }
type DeclinedStaticProjectSubObjectiveStateData = Array<[ categoryId: string, subcategoryGroups: Array<[ subCategoryId: string, subObjectiveGroups: DeclinedStaticProjectSubObjectiveDataItem[] ]> ]>

const AddDeclinedProjectSubObjectivesDialogDefaultProps = {
    projectId:                null,
    projectVersionId:         null,
    categoryId:               null,
    subCategoryId:            null,
    excludedTaxonomyIds:      [],
    mutationResultDataFields: null,
    showImportOptions:        false,
} satisfies Partial<AddDeclinedProjectSubObjectivesDialogProps>;

function AddDeclinedProjectSubObjectivesDialog( __props: AddDeclinedProjectSubObjectivesDialogProps ){

    const _props = { ...AddDeclinedProjectSubObjectivesDialogDefaultProps, ...__props };
    const {
              projectId,
              projectVersionId,
              categoryId:    selectedCategoryId,
              subCategoryId: selectedSubCategoryId,
              excludedTaxonomyIds,
              mutationResultDataFields,
              ...props
          } = _props;

    const [ availableSubObjectives, setAvailableSubObjectives ]                     = useState<DeclinedStaticProjectSubObjectiveDataItem[]>( [] ),
          [ availableSubObjectivesByCategory, setAvailableSubObjectivesByCategory ] = useState<DeclinedStaticProjectSubObjectiveStateData>( [] ),
          [ activeDependenciesOnImport, setActiveDependenciesOnImport ]             = useState<boolean>( false ),
          formikRef                                                                 = useRef<FormikProps<FormValues>>( null );


    const getAvailableProjectSubObjectivesReq = useQuery<{ subObjectives: CorvalSubObjective[] }>( DecisionEngineQueries.queries.GET_SUBOBJECTIVES( `
        id
        displayName
        description
        category { id name ordinal description }
        subCategory { id name ordinal description }
    ` ), {
        fetchPolicy: 'network-only',
        variables:   {
            where: {
                or: [
                    { contextOwner: { eq: CorvalContextOwner.Corval } },
                    { contextOwnerProjectId: { eq: projectId } }
                ]
            }
        }
    } );

    const getAvailableSubObjectivesWithActivitiesReq = useQuery<{ missingSubObjectivesForProjectVersion: CorvalSubObjective[] }>( ObjectiveQueries.queries.GET_DECLINED_PROJECT_SUBOBJECTIVES( `
        id
        displayName
        description
        category { id name ordinal description }
        subCategory { id name ordinal description }
        activities { activity{ id name description } }`
    ), {
        variables:   { projectVersionId },
        skip:        ( !projectId || !projectVersionId ),
        fetchPolicy: 'network-only'
    } );

    const isLoading = queriesLoading( [
        getAvailableProjectSubObjectivesReq,
        getAvailableSubObjectivesWithActivitiesReq
    ] );

    useEffect( () => {

        if( !queriesLoading( [ getAvailableProjectSubObjectivesReq, getAvailableSubObjectivesWithActivitiesReq ] ) &&
            queriesHaveData( [ getAvailableProjectSubObjectivesReq, getAvailableSubObjectivesWithActivitiesReq ] ) ){

            // Get sub objectives from the project, format records
            const subObjectiveItemsWithActivities = getAvailableSubObjectivesWithActivitiesReq.data.missingSubObjectivesForProjectVersion,
                  emptySubObjectiveItems          = getAvailableProjectSubObjectivesReq.data.subObjectives.filter( subObj => !( _.pluck( subObjectiveItemsWithActivities, 'id' ).includes( subObj.id ) ) );

            const formattedItems: DeclinedStaticProjectSubObjectiveDataItem[] = [];

            _.union( emptySubObjectiveItems, subObjectiveItemsWithActivities )
             .map( item => {
                 formattedItems.push( {
                         ...item,
                         activities: item[ 'activities' ] && !_.isEmpty( item[ 'activities' ] ) ? item[ 'activities' ].map( activity => activity.activity ) : null
                     } as unknown as DeclinedStaticProjectSubObjectiveDataItem
                 );
             } );

            // Format the sub objectives for taxonomy layout
            const categoricalItems = _.chain( formattedItems )
                                      .filter( ( s: DeclinedStaticProjectSubObjectiveDataItem ) => ( !selectedCategoryId || s.category.id === selectedCategoryId ) )
                                      .filter( ( s: DeclinedStaticProjectSubObjectiveDataItem ) => ( !selectedSubCategoryId || s.subCategory.id === selectedSubCategoryId ) )
                                      .filter( ( s: DeclinedStaticProjectSubObjectiveDataItem ) => !( excludedTaxonomyIds.includes( s.id ) ) )
                                      .groupBy( ( s: DeclinedStaticProjectSubObjectiveDataItem ) => s.category.id )
                                      .mapObject( categoryGroup => _.chain( categoryGroup ).groupBy( ( s: DeclinedStaticProjectSubObjectiveDataItem ) => s.subCategory.id ).pairs().value() )
                                      .pairs()
                                      .sortBy( ( [ categoryId, subcategoryGroup ] ) => {
                                              return _.first( _.first( subcategoryGroup )[ 1 ] ).category.name;
                                          }
                                      )
                                      .value();

            setAvailableSubObjectivesByCategory( categoricalItems );
            setAvailableSubObjectives( formattedItems );
        }

    }, [
        getAvailableProjectSubObjectivesReq,
        getAvailableSubObjectivesWithActivitiesReq
    ] );


    const generateGraphArgs = () => {

        const subObjectiveIds = [];

        _.mapObject( formikRef.current?.values ?? {}, ( isIncluded, selectedId ) => {
            if( isIncluded ) subObjectiveIds.push( selectedId );
        } );

        return ( {
            variables: {
                input: {
                    projectVersionId,
                    subObjectiveIds,
                    activateTiming: false
                }
            }
        } );
    };

    const handleOnComplete = ( data ) => {

        const subObjectiveIds = [];

        _.mapObject( formikRef.current?.values ?? {}, ( isIncluded, selectedId ) => {
            if( isIncluded ) subObjectiveIds.push( selectedId );
        } );

        const subObjectiveIdsWithActivities    = _.pluck( data.addProjectVersionSubObjectives?.staticProjectActivities, 'subObjectiveId' ),
              subObjectiveIdsWithoutActivities = _.difference( subObjectiveIds, subObjectiveIdsWithActivities ),
              addedEmptySubObjectives          = getAvailableProjectSubObjectivesReq.data.subObjectives.filter( ( { id } ) => ( subObjectiveIdsWithoutActivities.includes( id ) ) );

        runIfFunction( props.onComplete, {
            emptySubObjectives: addedEmptySubObjectives,
            addedActivities:    data.addProjectVersionSubObjectives?.staticProjectActivities ?? [] as CorvalStaticProjectActivity[],
            addedDependencies:  data.addProjectVersionSubObjectives?.ganttDependencies ?? [] as CorvalGanttDependency[],
            activeDependenciesOnImport
        } );
    };

    // Set Formik initial values for all ids of objectives that will exist on form
    const initialValues: FormValues = ( !isLoading && availableSubObjectives.length )
                                      ? _.chain( availableSubObjectives )
                                         .pluck( 'id' )
                                         .object( Array( availableSubObjectives.length ).fill( false ) )
                                         .value()
                                      : {};

    return (

        <Formik<FormValues>
            innerRef={ formikRef }
            initialValues={ initialValues }
            isInitialValid={ false }
            enableReinitialize={ true }
            validateOnChange={ true }
            validationSchema={ Yup.object( _.mapObject( initialValues, () => Yup.boolean() ) ) }
            onSubmit={ null }>

            { formik => (

                isLoading
                ? (
                    <StyledContainer padding={ Size.LG }>
                        <LoadingIndicator dimmer={ false } isActive={ true } label={ 'Loading Available Objectives...' }/>
                    </StyledContainer>
                ) : (
                    <QueryOpDialog
                        { ...props }
                        operationDisabled={ !projectId || !projectVersionId || isLoading || !formik.isValid }
                        operationIsHidden={ !projectId || !projectVersionId || ( availableSubObjectives.length === 0 ) }
                        mutation={ ObjectiveQueries.mutations.ADD_PROJECT_VERSION_SUBOBJECTIVES(
                            mutationResultDataFields?.activities ?? undefined,
                            mutationResultDataFields?.dependencies ?? undefined
                        ) }
                        graphArgs={ generateGraphArgs }
                        onComplete={ handleOnComplete }
                        queryOptions={ {
                            onCompleted: ( data ) => {
                                getAvailableSubObjectivesWithActivitiesReq.refetch();
                            }
                        } }
                        submitLabel={ 'Import Objectives' }
                        className={ 'no-padding' }
                        alignControls={ Align.CENTER }>

                        <Form>

                            <BasicDialogContainer
                                title={ 'Import Objectives to Project' }
                                contentMaxHeight={ '60dvh' }
                                contentPadding={ [ Size.MD, Size.MD, Size.LG, Size.MD ] }
                                overflow={ true }
                                scrolling={ true }
                                roundedTop>

                                <UseIf x={ availableSubObjectivesByCategory.length } eq={ 0 }>
                                    <BigFriendlyMessage
                                        title={ 'All Objectives Imported' }
                                        content={ (
                                            <>
                                                <p>There are no additional objectives available to add to this project.</p>
                                                <p>Check back later for more objectives your team might have created, or create new ones for this project using the provided tools.</p>
                                            </>
                                        ) }
                                    />
                                </UseIf>

                                {/* List of objectives to select */ }
                                { availableSubObjectivesByCategory.map( ( [ categoryId, subcategoryGroups ] ) => {

                                    const categoryName = availableSubObjectives.find( o => o.category.id === categoryId )?.category.name;

                                    return (
                                        <Segment raised className={ 'no-padding' }>

                                            <CategoryContainer
                                                isActive={ _.isString( selectedCategoryId ) }
                                                categoryId={ categoryId }
                                                categoryName={ categoryName }>

                                                <List relaxed={ 'very' } divided>

                                                    { subcategoryGroups.map( ( [ subCategoryId, availableSubObjectivesInSubCategory ] ) => {

                                                        const { name: subCategoryName, description: subCategoryDescription } = availableSubObjectives.find( o => o.subCategory.id === subCategoryId )?.subCategory;

                                                        return (
                                                            <List.Item>
                                                                <SubCategoryContainer
                                                                    isActive={ _.isString( selectedSubCategoryId ) }
                                                                    subCategoryId={ subCategoryId }
                                                                    subCategoryName={ subCategoryName }
                                                                    subCategoryDescription={ subCategoryDescription }>

                                                                    { availableSubObjectivesInSubCategory.map( availableSubObjective => (

                                                                        <Segment raised key={ availableSubObjective.id } className={ 'no-padding' }>

                                                                            <StyledContainer rounded={ true }
                                                                                             borderSize={ Size.MD }
                                                                                             padding={ [ Size.SM, Size.MD ] }
                                                                                             className={ classNames( {
                                                                                                 'border--hover':          !formik.values[ availableSubObjective.id ],
                                                                                                 'border--colored-purple': !formik.values[ availableSubObjective.id ],
                                                                                                 'border--colored-teal':   formik.values[ availableSubObjective.id ],
                                                                                             } ) }
                                                                                             onClick={ () => formik.setFieldValue( availableSubObjective.id, !formik.values[ availableSubObjective.id ] ) }>

                                                                                <Grid columns={ `auto 1fr` } columnGap={ '10px' } rowGap={ '0px' }>

                                                                                    <Cell className={ 'padding-md-vertical' }>

                                                                                        <Icon name={ ENTITY_ICONS.SUBOBJECTIVES.ENTITY } color={ 'teal' }/>

                                                                                    </Cell>
                                                                                    <Cell>
                                                                                        <ObjectiveSelectionLayout
                                                                                            onChange={ ( e, data ) => formik.setFieldValue( availableSubObjective.id, data.checked ) }
                                                                                            fieldValueName={ availableSubObjective.id }
                                                                                            item={ availableSubObjective as any }
                                                                                            showLastUpdated={ false }
                                                                                            includeDetailsLink={ false }
                                                                                            additionalContent={
                                                                                                <>
                                                                                                    <UseIf x={ _.isNull( availableSubObjective.activities ) } eq={ true }>

                                                                                                        <FinePrintMessage class={ 'text--center' }>This objective does not have any activities yet.</FinePrintMessage>

                                                                                                    </UseIf>
                                                                                                    <UseIf x={ _.isNull( availableSubObjective.activities ) } eq={ false }>

                                                                                                        <CollapsibleContentContainer
                                                                                                            borderless={ true }
                                                                                                            contentClasses={ 'no-padding' }
                                                                                                            onToggle={ ( isActive, collapseDurationMS, e ) => e.stopPropagation() }
                                                                                                            headerComponent={ props => (
                                                                                                                <Header color={ 'purple' } onClick={ props.onClick } as={ 'h5' } className={ classNames( {
                                                                                                                    'cursor--pointer':   true,
                                                                                                                    'opacity--100':      true,
                                                                                                                    'opacity-hover--80': true,
                                                                                                                    'no-margin':         true,
                                                                                                                } ) }>
                                                                                                                    <ItemWithIcon
                                                                                                                        gap={ '5px' }
                                                                                                                        middleAlign={ true }
                                                                                                                        iconComponent={
                                                                                                                            <Icon fitted
                                                                                                                                  size={ 'small' }
                                                                                                                                  color={ 'black' }
                                                                                                                                  name={ props.isActive ? 'chevron down' : 'chevron right' }
                                                                                                                            />
                                                                                                                        }>Supporting Activities</ItemWithIcon>
                                                                                                                </Header>
                                                                                                            ) }>

                                                                                                            <StyledContainer padding={ [ Size.SM, null ] } className={ classNames( 'margin-sm-top border-sm-top border--colored-lt-gray border-solid' ) }>
                                                                                                                <List divided={ true } relaxed={ 'very' }>

                                                                                                                    { availableSubObjective.activities?.map( activity => (
                                                                                                                        <List.Item key={ activity.id }>
                                                                                                                            <List.Icon>
                                                                                                                                <Icon name={ ENTITY_ICONS.ACTIVITIES.ENTITY } color={ 'teal' }/>
                                                                                                                            </List.Icon>
                                                                                                                            <List.Content>
                                                                                                                                <Header as={ 'h4' }>{ activity.name }</Header>
                                                                                                                                <FinePrintMessage>{ activity.description }</FinePrintMessage>
                                                                                                                            </List.Content>
                                                                                                                        </List.Item>
                                                                                                                    ) ) }

                                                                                                                </List>
                                                                                                            </StyledContainer>

                                                                                                        </CollapsibleContentContainer>
                                                                                                    </UseIf>
                                                                                                </>
                                                                                            }
                                                                                        />

                                                                                    </Cell>
                                                                                </Grid>

                                                                            </StyledContainer>

                                                                        </Segment>
                                                                    ) ) }

                                                                </SubCategoryContainer>

                                                            </List.Item>

                                                        );
                                                    } ) }

                                                </List>

                                            </CategoryContainer>

                                        </Segment>
                                    );
                                } ) }

                                { props.showImportOptions && (

                                    <StyledContainer
                                        padding={ Size.MD }
                                        position={ Position.STICKY }
                                        bgColor={ CorvalUIColor.WHITE }
                                        bottom={ `-50px` }
                                        style={ {
                                            margin:    `0 -20px -50px -20px`,
                                            borderTop: '1px solid var(--color-lightGrey)',
                                        } }>
                                        <Flexbox align={ FlexAlign.CENTER } justify={ FlexJustify.START } gutterSize={ Size.MD }>
                                            <FlexItem noGrow>
                                                <FieldWrapper>
                                                    <FieldLabel className={'no-margin'}>Import Options:</FieldLabel>
                                                </FieldWrapper>
                                            </FlexItem>
                                            <FlexItem>
                                                <Checkbox fitted={ true }
                                                          radio={ true }
                                                          id={ 'activeDependenciesOnImport' }
                                                          name={ 'activeDependenciesOnImport' }
                                                          checked={ activeDependenciesOnImport }
                                                          label={ `Activate All Dependencies` }
                                                          onClick={ ( e, data ) => {
                                                              setActiveDependenciesOnImport( data.checked );
                                                          } }
                                                />
                                            </FlexItem>
                                        </Flexbox>
                                    </StyledContainer>

                                ) }

                            </BasicDialogContainer>
                        </Form>

                    </QueryOpDialog>
                )
            ) }

        </Formik>
    );
}

const CategoryContainer = React.memo( ( props: {
    categoryId?: Uuid,
    categoryName: string,
    isActive?: boolean,
    children: any
} ) => (

    <CollapsibleContentContainer
        isActive={ props.isActive }
        borderless={ true }
        contentClasses={ 'padding-lg-left padding-md-vertical' }
        title={ props.categoryName }>

        { props.children }

    </CollapsibleContentContainer>
) );

const SubCategoryContainer = React.memo( ( props: {
    subCategoryId?: Uuid,
    subCategoryName: string,
    subCategoryDescription?: string,
    isActive?: boolean,
    children: any
} ) => (

    <CollapsibleContentContainer
        isActive={ props.isActive }
        borderless={ true }
        contentClasses={ 'padding-sm-right' }
        headerComponent={ headerProps => (
            <>
                <Header color={ 'purple' } onClick={ headerProps.onClick } as={ 'h3' } className={ classNames( {
                    'cursor--pointer':   true,
                    'opacity--100':      true,
                    'opacity-hover--80': true,
                } ) }>
                    <ItemWithIcon
                        gap={ '5px' }
                        middleAlign={ true }
                        iconComponent={
                            <Icon fitted
                                  size={ 'small' }
                                  color={ 'black' }
                                  name={ headerProps.isActive ? 'chevron down' : 'chevron right' }
                            />
                        }>{ props.subCategoryName }</ItemWithIcon>
                </Header>
                <FinePrintMessage fadeIn={ false } className={ 'padding-md-left padding-sm-top' }>{ props.subCategoryDescription }</FinePrintMessage>
            </>
        ) }>

        <Segment secondary>

            { props.children }

        </Segment>

    </CollapsibleContentContainer>
) );

export {
    AddDeclinedProjectSubObjectivesDialog,
    AddDeclinedProjectSubObjectivesDialogDefaultProps,
    type AddDeclinedProjectSubObjectivesDialogProps,
};
